import React, { useMemo } from 'react';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { observer } from 'mobx-react-lite';
import {
  Appearance,
  Stripe,
  StripeElements,
  loadStripe,
} from '@stripe/stripe-js';
import { useMst } from '../../../models/stores/Root';

type SubLoaderProps = {
  setElementsFunction: (stripe: Stripe, elements: StripeElements) => void;
};

const StripeSubLoader: React.FC<SubLoaderProps> = ({ setElementsFunction }) => {
  const stripe = useStripe();
  const elements = useElements();
  if (stripe && elements) {
    setElementsFunction(stripe, elements);
  }
  return null;
};

const StripeInvisibleLoader: React.FC = observer(() => {
  const rootStore = useMst();
  const { cart, configuration } = rootStore;
  const {
    payments: {
      stripeApiVersion,
      stripeBetaVersion,
      stripePublicKey,
      stripeMerchantId,
    },
    styles,
  } = configuration;

  const {
    stripeInfo: { clientSecretKey },
  } = cart;

  const stripePromise = useMemo(() => {
    if (
      !stripePublicKey ||
      !stripeApiVersion ||
      !stripeBetaVersion ||
      !stripeMerchantId
    ) {
      return Promise.resolve(null);
    }
    return loadStripe(stripePublicKey, {
      apiVersion: stripeApiVersion,
      betas: [stripeBetaVersion],
      stripeAccount: stripeMerchantId,
    });
  }, [stripePublicKey, stripeApiVersion, stripeBetaVersion, stripeMerchantId]);

  const stripeOptions = useMemo(() => {
    const appearance: Appearance = {
      variables: {
        colorPrimary: styles?.primaryButtonBackground || undefined,
        colorBackground: styles?.formFieldBackground || undefined,
        colorText: styles?.formFieldText || undefined,
        fontFamily: styles?.primaryFont || undefined,
      },
    };
    return {
      clientSecret: clientSecretKey || undefined,
      appearance,
    };
  }, [clientSecretKey, styles]);

  if (!cart.hasStripeSecret) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={stripeOptions}
      key={clientSecretKey}
    >
      <StripeSubLoader setElementsFunction={rootStore.setStripeElements} />
    </Elements>
  );
});

export default StripeInvisibleLoader;
